import { Container, Sprite, Texture } from 'pixi.js';
import { prefersReducedMotion } from './ui';

/** Gold, cream, pink, leaf green and sky: the game's own colours. */
const COLOURS = [0xffd36b, 0xffe8b0, 0xff6fc0, 0x7cc47f, 0x8fc8f0, 0xf2994a];

let paper: Texture | null = null;

/** One white paper rectangle, tinted per piece. */
function paperTexture(): Texture {
  if (paper) return paper;
  const canvas = document.createElement('canvas');
  canvas.width = 8;
  canvas.height = 14;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 8, 14);
    ctx.fillStyle = 'rgba(0,0,0,0.12)';
    ctx.fillRect(0, 7, 8, 7);
  }
  paper = Texture.from(canvas);
  return paper;
}

interface Piece {
  sprite: Sprite;
  vx: number;
  vy: number;
  spin: number;
  flutter: number;
  phase: number;
}

/**
 * A burst of confetti for a small win (a game won, a bonus taken): paper
 * shoots up and out from a point, then flutters down and fades. Add one
 * above the thing that was won and call {@link burst}; it cleans up after
 * itself. Cheap: a few dozen tinted sprites, one animation loop.
 */
export class Confetti extends Container {
  private pieces: Piece[] = [];
  private running = false;

  constructor() {
    super();
    this.eventMode = 'none';
  }

  /** Sweep away any confetti still falling (a new game is opening, so a
   *  win's paper never lands on the next game, win or lose). */
  clear(): void {
    this.pieces.forEach((p) => p.sprite.destroy());
    this.pieces = [];
  }

  /** Burst from `(x, y)` in this container's space; `count` pieces, about
   *  `spread` pixels across. Resolves when the last piece has gone. */
  burst(x: number, y: number, options: { count?: number; spread?: number } = {}): Promise<void> {
    if (prefersReducedMotion()) return Promise.resolve();
    const count = options.count ?? 60;
    const spread = options.spread ?? 260;
    for (let i = 0; i < count; i++) {
      const sprite = new Sprite(paperTexture());
      sprite.anchor.set(0.5);
      sprite.tint = COLOURS[i % COLOURS.length]!;
      sprite.scale.set(0.95 + Math.random() * 0.85);
      sprite.position.set(x + (Math.random() - 0.5) * 20, y + (Math.random() - 0.5) * 10);
      sprite.rotation = Math.random() * Math.PI;
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * 2.2;
      const speed = (spread / 260) * (5 + Math.random() * 7);
      this.pieces.push({
        sprite,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        spin: (Math.random() - 0.5) * 0.3,
        flutter: 0.08 + Math.random() * 0.1,
        phase: Math.random() * Math.PI * 2,
      });
      this.addChild(sprite);
    }
    return new Promise((resolve) => {
      if (this.running) {
        // Another burst is already animating: ride along with it.
        const wait = () => (this.running && !this.destroyed ? requestAnimationFrame(wait) : resolve());
        requestAnimationFrame(wait);
        return;
      }
      this.running = true;
      let last = performance.now();
      const frame = (now: number) => {
        // Its board was taken down mid-burst (a board on the overlay goes
        // once it has closed): stop, rather than move what's gone.
        if (this.destroyed) {
          this.pieces = [];
          this.running = false;
          resolve();
          return;
        }
        const dt = Math.min(3, (now - last) / 16.7);
        last = now;
        for (const p of this.pieces) {
          p.vy += 0.32 * dt;
          p.vx *= Math.pow(0.985, dt);
          p.vy = Math.min(p.vy, 4.2);
          p.phase += p.flutter * dt;
          p.sprite.x += (p.vx + Math.sin(p.phase) * 0.8) * dt;
          p.sprite.y += p.vy * dt;
          p.sprite.rotation += p.spin * dt;
          // Paper turning over: squash across as it tumbles.
          p.sprite.scale.x = Math.abs(Math.cos(p.phase)) * (p.sprite.scale.y || 1);
          if (p.vy > 2) p.sprite.alpha = Math.max(0, p.sprite.alpha - 0.012 * dt);
        }
        const gone = this.pieces.filter((p) => p.sprite.alpha <= 0);
        gone.forEach((p) => p.sprite.destroy());
        this.pieces = this.pieces.filter((p) => p.sprite.alpha > 0);
        if (this.pieces.length > 0) requestAnimationFrame(frame);
        else {
          this.running = false;
          resolve();
        }
      };
      requestAnimationFrame(frame);
    });
  }
}
